import {createAsyncThunk, isAnyOf} from '@reduxjs/toolkit'
import {RouterState} from 'redux-first-history'

import {isApiError} from '@shared/helpers/api'
import {hasEvery} from '@shared/helpers/array'
import {removeHyphen} from '@shared/helpers/format'
import {eq} from '@shared/helpers/lang'

import {changeLocation} from '@web/actions/application'
import {
    AddressBookRequestFailMessage,
    AddressBookRequestFailMessageGetter,
    AddressBookResponseCode,
    COMMON_ROUTER_KEY,
    DefaultAddressBookRequestFailMessage,
    isAddressBookRequestFailMessageGetterKey,
    isAddressBookRequestFailMessageKey,
    OrderSheetPageSet,
    PersonalCustomsClearanceErrorCase,
    PersonalCustomsClearanceScene,
    PersonalCustomsClearanceSceneType,
} from '@web/core/constants'
import {CUSTOMS} from '@web/core/urls'
import {getUrlInfoByWindow} from '@web/helpers/path'
import {validateAddress, checkPersonalCustomsCode} from '@web/helpers/policies'
import {personalCustomsClearanceUserInfoSelector} from '@web/selectors/personalCustomsClearance'
import {CommonState, pageSetInfoSelector} from '@web/slices/common/slice'
import {addressesInfoSelector, BaseAddressInfo, DeliveryInfoState} from '@web/slices/deliveryInfo/slice'
import {PersonalCustomsClearanceState} from '@web/slices/personalCustomsClearance/slice'
import {push} from '@web/slices/router'

import apis from './apis'

const actionType = 'personalCustomsClearanceManager'

const CustomsResult = {
    MATCH: 1,
    NOT_MATCH: 0,
    FAIL: -1,
} as const

type CustomsResultType = Constant<typeof CustomsResult>

type RootState = {
    router: RouterState
    common: CommonState
    deliveryInfo: DeliveryInfoState
    personalCustomsClearance: PersonalCustomsClearanceState
}

type ThunkApi<RejectValue = unknown> = {
    state: RootState
    rejectValue: RejectValue
}

type ActionPayload = {
    code?: string
    hash?: string
    customsResult?: CustomsResultType
    personalCustomCode?: string
    saved?: {
        id: string
        name: string
        phone: string
    }
    scene: PersonalCustomsClearanceSceneType
}

const getErrorScene = (errorTypes: string[]) => {
    if (
        errorTypes.includes(PersonalCustomsClearanceErrorCase.NOT_FOUND_CODE) ||
        errorTypes.includes(PersonalCustomsClearanceErrorCase.UNKNOWN) ||
        errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_ZIPCODE)
    ) {
        return PersonalCustomsClearanceScene.OTHER_NOT_FOUND_CODE
    }
    if (errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_CODE)) {
        return PersonalCustomsClearanceScene.OTHER_INVALID_CODE
    }
    if (
        hasEvery(
            [PersonalCustomsClearanceErrorCase.INVALID_NAME, PersonalCustomsClearanceErrorCase.INVALID_TEL_NO],
            errorTypes,
        )
    ) {
        return PersonalCustomsClearanceScene.OTHER_INVALID_INPUT
    }
    if (errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_NAME)) {
        return PersonalCustomsClearanceScene.OTHER_INVALID_NAME
    }
    if (errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_TEL_NO)) {
        return PersonalCustomsClearanceScene.OTHER_INVALID_TEL_NO
    }
    return PersonalCustomsClearanceScene.OTHER_NOT_FOUND_CODE
}

/**
 * 개인통관고유부호 조회
 * - 본인인 경우 통관 고유부호 전달
 * - 본인 혹은 타인인경우 전달한 통관 고유부호 일치여부 확인
 **/
async function getCustomsByApi(
    state: RootState,
    {
        entry,
        code,
    }: {
        // 사용처
        entry: string
        // 통관고유부호
        code?: string
    },
): Promise<{
    customsResult: CustomsResultType
    personalCustomCode: string
    hash: string
    errorTypes: string[]
}> {
    try {
        const {
            deliveryAddress: {receiverName, telNo1, zipCode},
        } = addressesInfoSelector(state)
        const {orderSheetPageSet} = pageSetInfoSelector(state)

        const result = await apis.getCode({
            entry,
            id: state.common.seqID,
            name: receiverName,
            phone: telNo1,
            code,
            zipCode,
            pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
        })
        return {
            customsResult: result.valid ? CustomsResult.MATCH : CustomsResult.NOT_MATCH,
            personalCustomCode: result.personalCustomCode,
            hash: result.hash,
            errorTypes: result.errorTypes,
        }
    } catch {
        return {
            customsResult: CustomsResult.FAIL,
            personalCustomCode: '',
            hash: '',
            errorTypes: [''],
        }
    }
}

/** 본인 입력인지 여부 확인 */
export const checkSelf = createAsyncThunk<ActionPayload, void, ThunkApi>(
    `${actionType}/checkSelf`,
    async (_, {getState}) => {
        const state = getState()
        const {receiverName, telNo1} = addressesInfoSelector(state).deliveryAddress ?? {receiverName: '', telNo1: ''}
        const {checkMFAUser} = personalCustomsClearanceUserInfoSelector(state)
        const {orderSheetPageSet} = pageSetInfoSelector(state)
        // 배송지를 변경할때마다 조회해야하는가?
        const {valid} = await apis.checkSelf({
            name: receiverName,
            phone: removeHyphen(telNo1),
            pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
        })

        return {
            scene: valid
                ? PersonalCustomsClearanceScene.IDLE
                : checkMFAUser
                  ? PersonalCustomsClearanceScene.OTHER_INPUT_MFA
                  : PersonalCustomsClearanceScene.OTHER_INPUT,
        }
    },
)

/**
 * 본인 - 동의하고 조회하기
 * - 개인통관고유부호는 개인일 경우에만 조회가 가능합니다.
 **/
export const getSelfCustoms = createAsyncThunk<
    ActionPayload & {
        valid: boolean
    },
    void,
    ThunkApi
>(`${actionType}/getSelfCustoms`, async (_, {getState}) => {
    const {customsResult, errorTypes, personalCustomCode, hash} = await getCustomsByApi(getState(), {
        entry: 'get',
    })

    const scene = (function () {
        if (eq(CustomsResult.MATCH, customsResult)) {
            return PersonalCustomsClearanceScene.SELF
        } else if (eq(CustomsResult.NOT_MATCH, customsResult)) {
            if (errorTypes.includes(PersonalCustomsClearanceErrorCase.NOT_FOUND_CODE)) {
                return PersonalCustomsClearanceScene.SELF_NOT_FOUND_CODE
            }
            if (errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_CODE)) {
                return PersonalCustomsClearanceScene.SELF_INVALID_CODE
            }
            if (errorTypes.includes(PersonalCustomsClearanceErrorCase.INVALID_ZIPCODE)) {
                return PersonalCustomsClearanceScene.SELF_INVALID_ZIPCODE
            }
            return PersonalCustomsClearanceScene.FAIL
        } else {
            // SIMPLE_FAIL
            return PersonalCustomsClearanceScene.FAIL
        }
    })()

    return {
        customsResult,
        scene,
        personalCustomCode,
        hash,
        valid: scene !== PersonalCustomsClearanceScene.FAIL,
    }
})

/** 개인통관고유부호 페이지 이동 */
export const goCustomsPage = createAsyncThunk<void, void, ThunkApi>(
    `${actionType}/goCustomsPage`,
    async (_, {dispatch}) => {
        dispatch(push(`${getUrlInfoByWindow().path}/${COMMON_ROUTER_KEY.personalCustomsClearancePage}`))
    },
)

/** 관세청에서 발급받기 */
export const issueCustoms = createAsyncThunk<ActionPayload, void, ThunkApi>(
    `${actionType}/issueCustoms`,
    async (_, {dispatch}) => {
        dispatch(
            changeLocation({
                type: 'NEW_TAB',
                url: CUSTOMS,
            }),
        )

        return {
            scene: PersonalCustomsClearanceScene.BRIDGE,
        }
    },
)

/** 발급정보 불러오기 */
export const loadIssuedCustoms = createAsyncThunk<ActionPayload, void, ThunkApi>(
    `${actionType}/loadIssuedCustoms`,
    async (_, {getState}) => {
        const {customsResult, personalCustomCode, hash} = await getCustomsByApi(getState(), {
            entry: 'load',
        })

        const scene = (function () {
            if (eq(customsResult, CustomsResult.MATCH)) {
                return PersonalCustomsClearanceScene.SELF
            } else if (eq(customsResult, CustomsResult.NOT_MATCH)) {
                return PersonalCustomsClearanceScene.SELF_INPUT
            } else {
                return PersonalCustomsClearanceScene.BRIDGE_FAIL
            }
        })()

        return {
            scene,
            customsResult,
            personalCustomCode,
            hash,
        }
    },
)

/**
 * 본인 - 개인통관 고유부호 입력하기
 * 타인 - 동의하고 입력하기
 **/
export const updateCustoms = createAsyncThunk<
    ActionPayload,
    {
        code: string
    },
    ThunkApi
>(`${actionType}/updateCustoms`, async ({code}, {getState, rejectWithValue}) => {
    if (!checkPersonalCustomsCode(code)) {
        return rejectWithValue({
            message: 'P로 시작하는 12자리 숫자로 입력해주세요.',
        })
    }

    const {customsResult, personalCustomCode, errorTypes, hash} = await getCustomsByApi(getState(), {
        code,
        entry: 'update',
    })

    if (eq(customsResult, CustomsResult.NOT_MATCH)) {
        const scene = getErrorScene(errorTypes)

        return {
            scene,
            customsResult,
            code,
            personalCustomCode,
            hash,
        }
    }

    const {selectedAddress} = addressesInfoSelector(getState())

    return {
        code,
        personalCustomCode,
        hash,
        customsResult,
        scene: PersonalCustomsClearanceScene.DONE,
        saved: {
            id: selectedAddress?.hash ?? '',
            name: selectedAddress?.receiverName ?? '',
            phone: selectedAddress?.telNo1 ?? '',
        },
    }
})

/** 직접 입력하기 */
export const setupCustomsInput = createAsyncThunk<ActionPayload, void, ThunkApi>(
    `${actionType}/setupCustomsInput`,
    async () => {
        return {
            scene: PersonalCustomsClearanceScene.SELF_INPUT,
        }
    },
)

/** 성공 - 입력하기 */
export const doneCustoms = createAsyncThunk<ActionPayload, {code: string; hash: string}, ThunkApi>(
    `${actionType}/doneCustoms`,
    async ({code, hash}, {getState}) => {
        const {selectedAddress} = addressesInfoSelector(getState())

        return {
            code,
            hash,
            scene: PersonalCustomsClearanceScene.DONE,
            saved: {
                id: selectedAddress?.hash ?? '',
                name: selectedAddress?.receiverName ?? '',
                phone: selectedAddress?.telNo1 ?? '',
            },
        }
    },
)

/** 개인통관고유부호 입력하지 않고 중단 */
export const cancelCustoms = createAsyncThunk<ActionPayload, void, ThunkApi>(
    `${actionType}/cancelCustoms`,
    async () => {
        return {
            scene: PersonalCustomsClearanceScene.IDLE,
        }
    },
)

/** 개인통관고유부호 연락처 수정 */
export const editCustomsAddressBook = createAsyncThunk<
    BaseAddressInfo,
    {
        receiverName: string
        telNo1: string
    },
    ThunkApi<{
        message: string
    }>
>(`${actionType}/editCustomsAddressBook`, async ({receiverName, telNo1}, {getState, rejectWithValue}) => {
    const {selectedAddress} = addressesInfoSelector(getState())
    const {orderSheetPageSet} = pageSetInfoSelector(getState())

    if (!selectedAddress) {
        return rejectWithValue({
            message: '선택된 배송지 정보가 없습니다.',
        })
    }
    if (receiverName === selectedAddress.receiverName && telNo1 === selectedAddress.telNo1) {
        // 입력이 동일하다면 update 하지 않습니다.
        return selectedAddress
    }

    const {type: validationResultType, message} = validateAddress({
        addressInfo: {
            ...selectedAddress,
            receiverName,
            telNo1,
        },
    })

    if (eq(validationResultType, 'INVALID')) {
        return rejectWithValue({
            message,
        })
    }

    const deliveryForm = {
        hash: selectedAddress.hash,
        addressName: selectedAddress.addressName,
        receiverName,
        zipCode: selectedAddress.zipCode,
        buildingManagementNo: selectedAddress.buildingManagementNo ?? '',
        locationX: selectedAddress.locationX ?? '',
        locationY: selectedAddress.locationY ?? '',
        baseAddress: selectedAddress.baseAddress,
        detailAddress: selectedAddress.detailAddress ?? '',
        isRoadNameAddress: selectedAddress.isRoadNameAddress,
        isRegistered: selectedAddress.isRegistered,
        telNo1,
        telNo2: selectedAddress.telNo2 ?? '',
        setAsPrimaryAddress: selectedAddress.isPrimaryAddress,
    }
    try {
        /**
         * [TODO] 배송지 액션 공통화 필요
         * 관리하는 state가 달라서 deliveryManager 액션 미사용 결정
         */
        // 선택된 주소가 미저장 주소인 경우
        if (!selectedAddress.isRegistered) {
            // 최대 배송지 개수 초과 여부 확인
            const {isAddible} = await apis.getAddressBook({
                pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
            })

            if (!isAddible) {
                // 15개 초과 등록 불가
                return rejectWithValue({
                    message: AddressBookRequestFailMessage[AddressBookResponseCode.MAX_REGISTERED_ADDRESS],
                })
            }
        }

        let response
        if (!selectedAddress.isRegistered) {
            // 배송지 저장
            response = await apis.addAddressBook({
                pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
                address: deliveryForm,
            })
        } else {
            // 배송지 수정
            response = await apis.editAddressBook({
                pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
                hash: selectedAddress.hash ?? '',
                address: deliveryForm,
            })
        }

        /**
         * 최근배송지 등록 / 등록배송지 수정 후 기등록된 최근배송지를 삭제합니다.
         * 최근배송지 삭제 실패의 경우에도 별다른 오류 처리 없이 주문서로 복귀합니다.
         */
        try {
            await apis.deleteAddressBook({
                pageSet: orderSheetPageSet ?? OrderSheetPageSet.MALL,
                hash: deliveryForm.hash,
            })
        } catch {}

        return {
            ...deliveryForm,
            hash: response.hash,
            isPrimaryAddress: deliveryForm.setAsPrimaryAddress,
        }
    } catch (e) {
        if (isApiError(e)) {
            const code = e.response?.data?.result?.code
            const usingServices = e.response?.data?.result?.usingServices

            if (code && typeof code === 'string') {
                let alertMessage = DefaultAddressBookRequestFailMessage
                if (isAddressBookRequestFailMessageKey(code)) {
                    alertMessage = AddressBookRequestFailMessage[code]
                } else if (isAddressBookRequestFailMessageGetterKey(code)) {
                    alertMessage = AddressBookRequestFailMessageGetter[code]({method: 'put', usingServices})
                }
                return rejectWithValue({message: alertMessage})
            }
        }
        throw e
    }
})

export const getCustomsByApis = isAnyOf(getSelfCustoms.fulfilled, loadIssuedCustoms.fulfilled, updateCustoms.fulfilled)

export const updateCustomsCodeCodes = isAnyOf(doneCustoms.fulfilled, updateCustoms.fulfilled)

export const initCustomsCodeCodes = isAnyOf(getSelfCustoms.fulfilled, goCustomsPage.fulfilled)

export const changePersonalCustomsClearanceScenes = isAnyOf(
    getSelfCustoms.fulfilled,
    checkSelf.fulfilled,
    issueCustoms.fulfilled,
    loadIssuedCustoms.fulfilled,
    updateCustoms.fulfilled,
    setupCustomsInput.fulfilled,
    doneCustoms.fulfilled,
    cancelCustoms.fulfilled,
)
